// SalesMedicineReminder.jsx
import { useEffect, useState } from 'react';
import { Modal, Button, Form, Table, CloseButton } from 'react-bootstrap';

export default function SalesMedicineReminder({ items = [], onSave }) {
  const [show, setShow] = useState(false);
  const [rows, setRows] = useState([]);      // [{id, selected, product, img, date, meta}]
  const [focused, setFocused] = useState(0);

  const open = () => setShow(true);
  const close = () => setShow(false);

  // Build modal rows from invoice items whenever the modal opens
  useEffect(() => {
    if (!show) return;
    const mapped = (items || []).map((p, idx) => ({
      id: p.id ?? idx,
      selected: false,
      product: p.productName || 'Product',
      img: p.imageUrl || '',                  // optional image if you have it
      date: '',                               // user will pick
      meta: {
        brand: p.brandName || '',
        pack: p.pack ? `${p.pack}` : '',
        description: p.hsn || ''              // repurpose any extra line if desired
      }
    }));
    setRows(mapped);
    setFocused(0);
  }, [show, items]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!show) return;
    const onKey = (e) => {
      if (e.key === 'F2') { e.preventDefault(); handleSave(); }
      else if (e.key === 'Escape') { e.preventDefault(); close(); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); setFocused(i => Math.min(i + 1, Math.max(rows.length - 1, 0))); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setFocused(i => Math.max(i - 1, 0)); }
      else if (e.code === 'Space') {
        e.preventDefault();
        setRows(rs => rs.map((r, i) => i === focused ? { ...r, selected: !r.selected } : r));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [show, rows, focused]);

  const handleDate = (idx, date) => {
    setRows(rs => rs.map((r, i) => (i === idx ? { ...r, date } : r)));
  };

  const handleSave = () => {
    const selected = rows.filter(r => r.selected);
    onSave?.(selected);
    close();
  };

  return (
    <>
      {/* Reminder tile button */}
      <button
        type="button"
        onClick={open}
        className="reminder-card btn p-4 w-full d-flex flex-column align-items-center justify-content-center"
        aria-label="Add Reminder"
      >
        <span className="plus-dot mb-2">+</span>
        <span className="fw-semibold text-purple">Add Reminder</span>
      </button>

      {/* Modal */}
      <Modal show={show} onHide={close} centered size="lg" backdrop="static" contentClassName="shadow-lg rounded-4">
        <Modal.Header className="border-0 pb-0">
          <div className="d-flex align-items-center w-100">
            <div>
              <h5 className="mb-0 fw-semibold">Add Reminder</h5>
              <div className="text-muted small">Set refill reminder for an item in cart</div>
            </div>
            <div className="ms-auto d-flex gap-2 align-items-center">
              <Button size="sm" variant="secondary" onClick={handleSave}>Save</Button>
              <CloseButton onClick={close} />
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          <Table responsive hover className="align-middle mb-3">
            <thead className="border-bottom">
              <tr>
                <th style={{ width: 40 }}>
                  <Form.Check
                    type="checkbox"
                    checked={rows.length > 0 && rows.every(r => r.selected)}
                    onChange={(e) =>
                      setRows(rs => rs.map(r => ({ ...r, selected: e.target.checked })))
                    }
                    aria-label="Select all"
                  />
                </th>
                <th style={{ width: 100 }}>Image</th>
                <th>Product</th>
                <th style={{ width: 220 }}>Set Reminder Date</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={4}>
                    <div className="py-5 text-center text-muted">
                      <div className="empty-illustration mx-auto mb-3" />
                      <div className="fw-semibold">We’ve Plenty Of Space For Your Data, We Promise!</div>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => (
                  <tr key={r.id} className={idx === focused ? 'table-active' : ''}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={!!r.selected}
                        onChange={() =>
                          setRows(rs => rs.map(x => x.id === r.id ? { ...x, selected: !x.selected } : x))
                        }
                        aria-label={`Select ${r.product}`}
                      />
                    </td>
                    <td>
                      {r.img ? (
                        <img src={r.img} alt="" className="rounded border" style={{ width: 56, height: 56, objectFit: 'cover' }} />
                      ) : (
                        <div className="ratio ratio-1x1 rounded bg-light border" style={{ width: 56 }} />
                      )}
                    </td>
                    <td>
                      <div className="fw-semibold">{r.product}</div>
                      {(r.meta?.brand || r.meta?.pack) && (
                        <div className="text-muted small">{[r.meta.brand, r.meta.pack ? `${r.meta.pack} per pack` : ''].filter(Boolean).join(' | ')}</div>
                      )}
                      {r.meta?.description && (
                        <div className="text-muted small">{r.meta.description}</div>
                      )}
                    </td>
                    <td>
                      <Form.Control
                        type="date"
                        value={r.date || ''}
                        onChange={(e) => handleDate(idx, e.target.value)}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>


        </Modal.Body>
      </Modal>
    </>
  );
}
